import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { FolderOpen, ClipboardList, Lock, Trophy, Users, Activity, Coins, Clock } from "lucide-react";
import api from "@/lib/api";
import { StatTile, SectionHeader, DLLoading, EmptyState, DLButton, Slogan, SimulationDisclaimer } from "@/components/dl/ui";
import EntitlementGate from "@/components/dl/EntitlementGate";

export default function DLCaseHQ() {
  const { caseId, caseData, team, entitled, entitlement } = useOutletContext();
  const [data, setData] = useState({ loading: true });

  useEffect(() => {
    if (!team) { setData({ loading: false }); return; }
    (async () => {
      try {
        const [st, ev, pb] = await Promise.all([
          api.get(`/dl/cases/${caseId}/standings`),
          api.get(`/dl/teams/${team.dl_team_id}/score-events`),
          api.get(`/dl/teams/${team.dl_team_id}/playbook`),
        ]);
        const row = (st.data.standings || []).find((r) => r.dl_team_id === team.dl_team_id) || {};
        setData({ loading: false, row, totals: ev.data.totals, events: ev.data.score_events || [], playbookCount: (pb.data || []).length });
      } catch { setData({ loading: false }); }
    })();
  }, [caseId, team]);

  if (!entitled) return <EntitlementGate entitlement={entitlement} />;

  if (!team) return (
    <EmptyState icon={Users} title="You're not on a team yet"
      desc="Every case is played as a defense team. Create your own team or accept an invite to get in the game."
      action={<Link to="team"><DLButton data-testid="dl-hq-go-team"><Users className="w-4 h-4" /> Build Your Team</DLButton></Link>}
      testid="dl-hq-no-team" />
  );

  if (data.loading) return <DLLoading label="Loading Case HQ…" />;

  const row = data.row || {};
  const nextCta = data.playbookCount === 0
    ? { to: "playbook", label: "Build Your Playbook", icon: ClipboardList }
    : (row.locked_play_count || 0) === 0
      ? { to: "plays", label: "Call the Play", icon: Lock }
      : { to: "standings", label: "Check the Standings", icon: Trophy };
  const NIcon = nextCta.icon;

  return (
    <div className="space-y-8">
      <SimulationDisclaimer />
      <SectionHeader kicker={`Case HQ · ${team.team_name}`} title="What's the next move?"
        desc="Your command center for this case — where you stand, what you've committed, and what to do next."
        right={<Link to={nextCta.to}><DLButton data-testid="dl-hq-primary-cta"><NIcon className="w-4 h-4" /> {nextCta.label}</DLButton></Link>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile testid="dl-hq-total" label="Total Points" value={row.total_points ?? 0} accent />
        <StatTile testid="dl-hq-playbook-pts" label="Playbook Points" value={row.playbook_points ?? 0} />
        <StatTile testid="dl-hq-case-pts" label="Case Points" value={row.case_points ?? 0} />
        <StatTile testid="dl-hq-rank" label="Rank" value={row.rank ? `#${row.rank}` : "—"} accent />
        <StatTile testid="dl-hq-cap" label="Cap Points Left" value={row.cap_remaining ?? "—"} />
        <StatTile testid="dl-hq-hours" label="Attorney Hours Left" value={row.attorney_hours_remaining ?? "—"} />
        <StatTile testid="dl-hq-locked" label="Locked Plays" value={row.locked_play_count ?? 0} />
        <StatTile testid="dl-hq-confirmed" label="Confirmed Plays" value={row.confirmed_play_count ?? 0} accent />
      </div>

      {/* Quick rooms */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { to: "record", label: "Study the Record", icon: FolderOpen, sub: "Official case file" },
          { to: "film", label: "Watch the Film", icon: Activity, sub: "Commissioner analysis" },
          { to: "resources", label: "Manage Resources", icon: Coins, sub: "Cap & attorney hours" },
          { to: "plays", label: "Locked Plays", icon: Lock, sub: "Your committed calls" },
        ].map((r) => (
          <Link key={r.to} to={r.to} data-testid={`dl-hq-link-${r.to}`}
            className="dl-panel rounded-md p-4 hover:border-gold/50 transition-colors group">
            <r.icon className="w-5 h-5 text-gold mb-2" />
            <div className="font-display font-semibold text-white group-hover:text-gold transition-colors">{r.label}</div>
            <div className="text-xs text-white/45 mt-0.5">{r.sub}</div>
          </Link>
        ))}
      </div>

      {/* Recent activity */}
      <div>
        <SectionHeader kicker="Recent Activity" title="On the record" className="mb-4" />
        {(!data.events || data.events.length === 0) ? (
          <EmptyState icon={Activity} title="No scoring activity yet" desc="Playbook and Case Points awarded by the Commissioner will appear here." testid="dl-hq-no-activity" />
        ) : (
          <div className="dl-panel rounded-lg divide-y divide-white/5">
            {data.events.slice(-8).reverse().map((e) => (
              <div key={e.score_event_id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid="dl-hq-activity-row">
                <div className="min-w-0">
                  <span className={`text-xs font-mono font-semibold ${e.score_type === "CASE" ? "text-gold" : "text-sky-300"}`}>{e.score_type}</span>
                  <span className="text-sm text-white/70 ml-2">{e.reason}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`font-display font-bold ${Number(e.points) < 0 ? "text-red-400" : "text-emerald-300"}`}>{Number(e.points) > 0 ? "+" : ""}{e.points}</span>
                  <span className="text-[10px] text-white/30 font-mono hidden sm:flex items-center gap-1"><Clock className="w-3 h-3" />{(e.awarded_at || "").slice(0, 10)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Slogan className="block text-center text-white/30 pt-4">Live with the consequences. No Monday-morning quarterbacking.</Slogan>
    </div>
  );
}
